/**
 * 案件・承認・確定（F4）の oRPC contract。
 *
 * 案件の読取 5 本（一覧・変更の履歴・要求・差分・憲章）は `specGraph` に、確定・承認・承認の一覧・担当者かの照合・用語の備考の 5 本は `specPj` に載る。
 * 憲章だけは仕様グラフの外（Firestore `spec_charters`）が正だが、案件を開いた人が最初に読む案件の
 * 読取なので、ほかの案件の読取と同じ並びに置く。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件の読取（一覧・変更の履歴・要求・差分・憲章）と、確定・承認・承認の一覧・担当者かの照合の procedure の入出力を oRPC contract として定義する。
 * @graph-connects none
 */
import { oc } from '@orpc/contract';
import { ApproveTermChangesInputSchema, ApproveTermChangesOutputSchema, GetChangeHistoryInputSchema, GetChangeHistoryOutputSchema, GetPjCharterInputSchema, GetPjCharterOutputSchema, GetPjDiffInputSchema, GetPjDiffOutputSchema, ListTermChangesAcrossPjsInputSchema, ListTermChangesAcrossPjsOutputSchema, ListPjRequirementsInputSchema, ListPjRequirementsOutputSchema, ListPjsInputSchema, ListPjsOutputSchema, MergePjInputSchema, MergePjOutputSchema, UpdateTermNoteInputSchema, UpdateTermNoteOutputSchema, } from '../elements/index.js';
import { GetApprovalAccessInputSchema, GetApprovalAccessOutputSchema, } from '../spec-pj-term-approval.js';
import { SHARED_ERRORS, base } from './common.js';
/**
 * 案件の読取 procedure（`specGraph` に載る）。
 * @graph-connects none
 */
export const pjReadContract = {
    listPjs: base.input(ListPjsInputSchema).output(ListPjsOutputSchema),
    getChangeHistory: base.input(GetChangeHistoryInputSchema).output(GetChangeHistoryOutputSchema),
    listPjRequirements: base
        .input(ListPjRequirementsInputSchema)
        .output(ListPjRequirementsOutputSchema),
    getPjDiff: base.input(GetPjDiffInputSchema).output(GetPjDiffOutputSchema),
    getPjCharter: base.input(GetPjCharterInputSchema).output(GetPjCharterOutputSchema),
};
/**
 * PJ 操作の procedure が共有する builder。実行者の限定（`FORBIDDEN`）を意味付けする。
 *
 * 読取と同じ `base` を使わないのは、`FORBIDDEN` が読取では起こらない（認証さえ通れば
 * 全員読める）のに対し、確定と承認では**実行者の限定そのものが機能**だからである。
 * @graph-connects none
 */
const pjBase = oc.errors({
    ...SHARED_ERRORS,
    FORBIDDEN: { message: 'この操作を実行できるのは限られた担当者だけです' },
});
/**
 * PJ 操作 feature の oRPC contract（5 本）。
 *
 * 確定（`merge`）は spec グラフのマスターを実際に書き換える唯一の procedure である。
 * 承認（`approveTermChanges`）は変更の行の状態を倒すだけで、マスターは書き換えない。
 * 承認の一覧（`listTermChangesAcrossPjs`）は未確定の全案件を走査して判断するための担当者の画面で、
 * 担当者にしか返さない（`FORBIDDEN`）。本人が担当者かは `getApprovalAccess` がロールの照合だけで
 * 答え、サイドバーの入口を出し分ける。入力の案件番号は number である（決定 2）。
 * 用語の備考（`updateTermNote`）は人が画面からだけ書く欄で、MCP に対の口を置かない。
 * @graph-connects none
 */
export const specPjContract = {
    getApprovalAccess: base.input(GetApprovalAccessInputSchema).output(GetApprovalAccessOutputSchema),
    merge: pjBase.input(MergePjInputSchema).output(MergePjOutputSchema),
    approveTermChanges: pjBase
        .input(ApproveTermChangesInputSchema)
        .output(ApproveTermChangesOutputSchema),
    listTermChangesAcrossPjs: pjBase
        .input(ListTermChangesAcrossPjsInputSchema)
        .output(ListTermChangesAcrossPjsOutputSchema),
    updateTermNote: pjBase.input(UpdateTermNoteInputSchema).output(UpdateTermNoteOutputSchema),
};
//# sourceMappingURL=pj.js.map